import React, { Component } from "react";
import styled from "styled-components/macro";

import {
  Avatar,
  Box,
  Checkbox,
  Chip as MuiChip,
  IconButton,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  Tooltip,
  Typography,
} from "@material-ui/core";

import { green, orange, red } from "@material-ui/core/colors";

import {
  AttachMoney as AttachMoneyIcon,
  Assessment as AssessmentIcon,
  Autorenew as AutorenewIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  RemoveRedEye as RemoveRedEyeIcon,
} from "@material-ui/icons";

import { Link } from "react-router-dom";
import { spacing } from "@material-ui/system";

import { getPayments } from "../services/paymentServices";
import { withTranslation } from "react-i18next";
import { formatCurrency, formatDate } from "../utils/localization";

const Paper = styled(MuiPaper)`
  ${spacing};

  overflow: hidden;
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.08);
`;

const Chip = styled(MuiChip)`
  ${spacing};

  background: ${(props) => props.shipped && green[500]};
  background: ${(props) => props.processing && orange[700]};
  background: ${(props) => props.cancelled && red[500]};
  color: ${(props) => props.theme.palette.common.white};
`;

const Spacer = styled.div`
  flex: 1 1 100%;
`;

const ToolbarTitle = styled.div`
  min-width: 150px;
`;

const SummaryStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  padding: 20px 24px 0;
`;

const SummaryCard = styled.div`
  display: flex;
  gap: 14px;
  align-items: center;
  padding: 16px 18px;
  border-radius: 18px;
  background: ${(props) => props.background || "#f8fafc"};
  border: 1px solid rgba(148, 163, 184, 0.16);
`;

const SummaryLabel = styled(Typography)`
  color: #64748b;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const SummaryValue = styled(Typography)`
  font-weight: 700;
`;

const HeaderCell = styled(TableCell)`
  && {
    background: #f8fbff;
    color: #475569;
    font-size: 0.72rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    border-bottom: 1px solid rgba(148, 163, 184, 0.16);
  }
`;

const DataCell = styled(TableCell)`
  && {
    border-bottom: 1px solid rgba(226, 232, 240, 0.8);
  }
`;

const ActionIconButton = styled(IconButton)`
  && {
    background: rgba(29, 78, 137, 0.08);
    color: #1d4e89;

    &:hover {
      background: rgba(29, 78, 137, 0.16);
    }
  }
`;

class PaymentTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      rows: [],
      order: "asc",
      orderBy: "customer",
      selected: [],
      page: 0,
      rowsPerPage: 10,
      props: props,
    };
  }

  pad2(n) {
    return (n < 10 ? "0" : "") + n;
  }
  dateFormatter(dates) {
    var date = new Date(dates);
    var month = this.pad2(date.getMonth() + 1); //months (0-11)
    var day = this.pad2(date.getDate()); //day (1-31)
    var year = date.getFullYear();

    var todayformattedDate = year + "-" + month + "-" + day;
    return todayformattedDate;
  }

  formatCutoffDate(calculatedDate) {
    if (!calculatedDate) {
      return "";
    }

    if (typeof calculatedDate === "string" && calculatedDate.length >= 10) {
      return calculatedDate.slice(0, 10);
    }

    return this.dateFormatter(calculatedDate);
  }

  async getOptions() {
    const res = await getPayments();
    const data = (res.data || []).filter(
      (refSource) => refSource.originSystem === "MONETIZATION"
    );
    this.setState({ rows: data });
  }

  componentDidMount() {
    this.getOptions();
  }

  formatCurrency(value) {
    return formatCurrency(value);
  }

  render() {
    const { t } = this.props;
    const handleRequestSort = (event, property) => {
      const isAsc =
        this.state.orderBy === property && this.state.order === "asc";
      this.setState({ order: isAsc ? "desc" : "asc" });
      this.setState({ orderBy: property });
    };

    const handleSelectAllClick = (event) => {
      if (event.target.checked) {
        const newSelecteds = this.state.rows.map((n) => n.id);
        this.setState({ selected: newSelecteds });
        return;
      }
      this.setState({ selected: [] });
    };

    const handleClick = (event, id) => {
      const selectedIndex = this.state.selected.indexOf(id);
      let newSelected = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(this.state.selected, id);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(this.state.selected.slice(1));
      } else if (selectedIndex === this.state.selected.length - 1) {
        newSelected = newSelected.concat(this.state.selected.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          this.state.selected.slice(0, selectedIndex),
          this.state.selected.slice(selectedIndex + 1)
        );
      }

      this.setState({ selected: newSelected });
    };

    const handleChangePage = (event, newPage) => {
      this.setState({ page: newPage });
    };

    const handleChangeRowsPerPage = (event) => {
      this.setState({ rowsPerPage: parseInt(event.target.value, 10) });
      this.setState({ page: 0 });
    };

    const isSelected = (id) => this.state.selected.indexOf(id) !== -1;

    const emptyRows =
      this.state.rowsPerPage -
      Math.min(
        this.state.rowsPerPage,
        this.state.rows.length - this.state.page * this.state.rowsPerPage
      );

    const totalGross = this.state.rows.reduce(
      (total, row) => total + Number(row.grossPay || 0),
      0
    );
    const totalNet = this.state.rows.reduce(
      (total, row) => total + Number(row.pay || 0),
      0
    );
    const pendingCount = this.state.rows.filter(
      (row) => row.transactionStatus === null || row.transactionStatus?.id === 2
    ).length;
    const creditedCount = this.state.rows.filter(
      (row) => row.transactionStatus?.id === 1
    ).length;

    function getComparator(order, orderBy) {
      return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
    }

    function stableSort(array, comparator) {
      const stabilizedThis = array.map((el, index) => [el, index]);
      stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
      });
      return stabilizedThis.map((el) => el[0]);
    }

    function descendingComparator(a, b, orderBy) {
      if (b[orderBy] < a[orderBy]) {
        return -1;
      }
      if (b[orderBy] > a[orderBy]) {
        return 1;
      }
      return 0;
    }

    function EnhancedTableHead(props) {
      const {
        onSelectAllClick,
        order,
        orderBy,
        numSelected,
        rowCount,
        onRequestSort,
      } = props;
      const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
      };

      const headCells = [
        { id: "id", alignment: "right", label: "ID" },
        { id: "reference", alignment: "left", label: t("payments.reference") },
        {
          id: "calculatedDate",
          alignment: "left",
          label: t("payments.cutoffDate"),
        },
        { id: "quantity", alignment: "left", label: t("payments.viewCount") },
        { id: "cost", alignment: "left", label: t("payments.cost") },
        { id: "grossPay", alignment: "left", label: t("payments.gross") },
        { id: "pay", alignment: "left", label: t("payments.net") },
        { id: "tax", alignment: "left", label: t("payments.tax") },
        { id: "retention", alignment: "left", label: t("payments.retention") },
        {
          id: "transactionStatus",
          alignment: "left",
          label: t("common.status"),
        },
        { id: "actions", alignment: "center", label: t("common.actions") },
      ];

      return (
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                indeterminate={numSelected > 0 && numSelected < rowCount}
                checked={rowCount > 0 && numSelected === rowCount}
                onChange={onSelectAllClick}
                inputProps={{ "aria-label": "select all" }}
              />
            </TableCell>
            {headCells.map((headCell) => (
              <HeaderCell
                key={headCell.id}
                align={headCell.alignment}
                padding={headCell.disablePadding ? "none" : "default"}
                sortDirection={orderBy === headCell.id ? order : false}
              >
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : "asc"}
                  onClick={createSortHandler(headCell.id)}
                >
                  {headCell.label}
                </TableSortLabel>
              </HeaderCell>
            ))}
          </TableRow>
        </TableHead>
      );
    }

    let EnhancedTableToolbar = (props) => {
      return (
        <Toolbar>
          <ToolbarTitle>
            <Typography variant="h6" id="tableTitle">
              {t("payments.settlementSummary")}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t("payments.tableDescription")}
            </Typography>
          </ToolbarTitle>
          <Spacer />
        </Toolbar>
      );
    };

    return (
      <div>
        <Paper>
          <SummaryStrip>
            <SummaryCard background="linear-gradient(135deg, rgba(29, 78, 137, 0.12), rgba(95, 168, 211, 0.18))">
              <Avatar style={{ background: "#1d4e89" }}>
                <AssessmentIcon />
              </Avatar>
              <div>
                <SummaryLabel variant="body2">
                  {t("payments.registered")}
                </SummaryLabel>
                <SummaryValue variant="h5">
                  {this.state.rows.length}
                </SummaryValue>
              </div>
            </SummaryCard>
            <SummaryCard>
              <Avatar style={{ background: "#0f766e" }}>
                <AttachMoneyIcon />
              </Avatar>
              <div>
                <SummaryLabel variant="body2">
                  {t("payments.gross")}
                </SummaryLabel>
                <SummaryValue variant="h5">
                  {this.formatCurrency(totalGross)}
                </SummaryValue>
              </div>
            </SummaryCard>
            <SummaryCard>
              <Avatar style={{ background: "#15803d" }}>
                <CheckCircleOutlineIcon />
              </Avatar>
              <div>
                <SummaryLabel variant="body2">{t("payments.net")}</SummaryLabel>
                <SummaryValue variant="h5">
                  {this.formatCurrency(totalNet)}
                </SummaryValue>
              </div>
            </SummaryCard>
            <SummaryCard>
              <Avatar style={{ background: "#c2410c" }}>
                <AutorenewIcon />
              </Avatar>
              <div>
                <SummaryLabel variant="body2">
                  {t("payments.status.processing")}
                </SummaryLabel>
                <SummaryValue variant="h5">
                  {t("payments.processingSummary", {
                    pending: pendingCount,
                    credited: creditedCount,
                  })}
                </SummaryValue>
              </div>
            </SummaryCard>
          </SummaryStrip>
          <EnhancedTableToolbar numSelected={this.state.selected.length} />
          <TableContainer>
            <Table
              aria-labelledby="tableTitle"
              size={"medium"}
              aria-label="enhanced table"
            >
              <EnhancedTableHead
                numSelected={this.state.selected.length}
                order={this.state.order}
                orderBy={this.state.orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={this.state.rows.length}
              />
              <TableBody>
                {stableSort(
                  this.state.rows,
                  getComparator(this.state.order, this.state.orderBy)
                )
                  .slice(
                    this.state.page * this.state.rowsPerPage,
                    this.state.page * this.state.rowsPerPage +
                      this.state.rowsPerPage
                  )
                  .map((row, index) => {
                    const isItemSelected = isSelected(row.id);
                    const labelId = `enhanced-table-checkbox-${index}`;

                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={`${row.id}-${index}`}
                        selected={isItemSelected}
                        style={{
                          backgroundColor:
                            index % 2 === 0 ? "#ffffff" : "#fbfdff",
                        }}
                      >
                        <DataCell padding="checkbox">
                          <Checkbox
                            checked={isItemSelected}
                            inputProps={{ "aria-labelledby": labelId }}
                            onClick={(event) => handleClick(event, row.id)}
                          />
                        </DataCell>

                        <DataCell align="right">{row.id}</DataCell>

                        <DataCell align="left">{row.reference}</DataCell>

                        <DataCell align="left">
                          {this.formatCutoffDate(row.calculatedDate)}
                        </DataCell>

                        <DataCell align="left">{row.quantity}</DataCell>

                        <DataCell align="left">
                          {this.formatCurrency(row.cost)}
                        </DataCell>
                        <DataCell>{this.formatCurrency(row.grossPay)}</DataCell>
                        <DataCell align="left">
                          {this.formatCurrency(row.pay)}
                        </DataCell>
                        <DataCell align="left">
                          {this.formatCurrency(row.tax)}
                        </DataCell>
                        <DataCell align="left">
                          {this.formatCurrency(row.retention)}
                        </DataCell>
                        <DataCell>
                          {row.transactionStatus === null ? (
                            <Chip
                              size="small"
                              mr={1}
                              mb={1}
                              label={t("payments.status.processing")}
                              processing={1}
                            />
                          ) : row.transactionStatus.id === 1 ? (
                            <Chip
                              size="small"
                              mr={1}
                              mb={1}
                              label={t("payments.status.paid")}
                              shipped={1}
                            />
                          ) : row.transactionStatus.id === 2 ? (
                            <Chip
                              size="small"
                              mr={1}
                              mb={1}
                              label={t("payments.status.processing")}
                              processing={1}
                            />
                          ) : row.transactionStatus.id === 3 ? (
                            <Chip
                              size="small"
                              mr={1}
                              mb={1}
                              label={t("payments.status.rejected")}
                              cancelled={1}
                            />
                          ) : (
                            ""
                          )}
                        </DataCell>
                        <DataCell padding="none" align="center">
                          <Box mr={2}>
                            <Link
                              to={{
                                pathname: "/invoiceDetail",
                                state: {
                                  idInvoice: row.id,
                                  quantity: row.quantity,
                                  cost: row.cost,
                                  grossPay: row.grossPay,
                                  pay: row.pay,
                                  tax: row.tax,
                                  retention: row.retention,
                                  reference: row.reference,
                                  calculatedDate: row.calculatedDate
                                    ? formatDate(row.calculatedDate)
                                    : "",
                                  payedDate: row.payedDate
                                    ? formatDate(row.payedDate)
                                    : "",
                                },
                              }}
                            >
                              <Tooltip title={t("payments.viewDetails")}>
                                <ActionIconButton aria-label="details">
                                  <RemoveRedEyeIcon />
                                </ActionIconButton>
                              </Tooltip>
                            </Link>
                          </Box>
                        </DataCell>
                      </TableRow>
                    );
                  })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={8} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            style={{ borderTop: "1px solid rgba(226, 232, 240, 0.9)" }}
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={this.state.rows.length}
            rowsPerPage={this.state.rowsPerPage}
            page={this.state.page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </Paper>
      </div>
    );
  }
}

export default withTranslation()(PaymentTable);
